import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { LoadingType } from "../../../types/CommonTypes";
import { getActivePracticesKPIData, getActivePracticesKPIDetailsList, getActiveUsersKPIData, getActiveUsersKPIDetailsList, getIvrBreakdownData, getMonthlyIvrCountsTwelveMonths, getMonthlyOrderCountsTwelveMonths, getOrderBreakdownData, getTotalPatientsKPIData, getWeeklyOrdersKPIData, getWeeklyRecordTxPercentage, getWeeklySalesKPIData, getWeeklySalesKPIDetailsList } from "../../../service/DashboardService";
import { updateElementValue } from "../../../utils/updateElementValue";
import { useAppSelector } from "../redux-hooks";

export type UserStats = {
  activeUsers: number;
  userComparison: number;
  fourWeeksRangeUserCounts: CountRange[];
};

export type PracticeStats = {
  activePractices: number;
  fourWeekRangePracticeCounts: CountRange[];
  practiceComparison: number;
};

export type IVRStats = {
  drafted: number;
  submitted: number;
  needMoreInfo: number;
  denied: number;
  verified: number;
  canceled: number;
  preAuth: number;
  total: number;
  monthlyIvrCounts: number[];
  monthlyVerifiedIvrCounts: number[];
};

export type OrderStats = {
  placed: number;
  shipped: number;
  delayed: number;
  delivered: number;
  canceled: number;
  returned: number;
  processing: number;
  pending: number;
  total: number;
  monthlyOrderCounts: number[];
  monthlyDeliveredCounts: number[];
  weeklyOrders: number;
  weeklyOrderCounts: CountRange[];
  orderComparison: number;
};

export type SalesStats = {
  weeklySales: number;
  salesComparison: number;
  weeklySalesCounts: CountRange[];
};

export type PatientStats = {
  totalPatients: number;
  fourWeeksRangePatientCounts: CountRange[];
  patientComparison: number;
};

export type CountRange = {
  fromDate: string;
  toDate: string;
  count: number;
}

export type RecordTxStats = {
  weeklyRecordTx: number;
  weeklyRecordTxComparison: number;
  weeklyRecordTxCounts: CountRange[]
};


export type DashboardData = {
  userStats: UserStats;
  practiceStats: PracticeStats;
  ivrStats: IVRStats;
  orderStats: OrderStats;
  salesStats: SalesStats;
  patientStats: PatientStats;
  lastUpdatedAt: string;
};

// CS-1679
export type SearchKpiRequest = {
  downloadState: boolean;
  currentPage: number;
  itemsPerPage: number;
};

type DashboardState = {
  dashboardData: DashboardData | null;
  dashboardDataFetchStatus: LoadingType;
  userStats: UserStats | null;
  practiceStats: PracticeStats | null;
  salesStats: SalesStats | null;
  patientStats: PatientStats | null;
  weeklyOrdersStats: OrderStats | null;
  orderBreakdownStats: OrderStats | null;
  monthlyOrderCountTwelveMonths: OrderStats | null;
  weeklyIvrStats: IVRStats | null;
  ivrBreakdownStats: IVRStats | null;
  monthlyIvrCountTwelveMonths: IVRStats | null;
  weeklyRecordTxPercentageStats: RecordTxStats | null;
  activeUsersFetchStatus: LoadingType;
  activePracticesFetchStatus: LoadingType;
  totalPatientsFetchStatus: LoadingType;
  weeklySalesFetchStatus: LoadingType;
  weeklyOrdersFetchStatus: LoadingType;
  orderBreakdownFetchStatus: LoadingType;
  monthlyOrderCountFetchStatus: LoadingType;
  weeklyIvrFetchStatus: LoadingType;
  ivrBreakdownFetchStatus: LoadingType;
  monthlyIvrCountFetchStatus: LoadingType;
  weeklyRecordTxPercentageFetchStatus: LoadingType;
};

const initialState = {
  dashboardData: null,
  userStats: null,
  practiceStats: null,
  salesStats: null,
  patientStats: null,
  weeklyOrdersStats: null,
  orderBreakdownStats: null,
  ivrBreakdownStats: null,
  weeklyIvrStats: null, // CS-1984
  monthlyOrderCountTwelveMonths: null,
  monthlyIvrCountTwelveMonths: null,
  weeklyRecordTxPercentageStats: null,
  dashboardDataFetchStatus: 'idle',
  activeUsersFetchStatus: 'idle',
  activePracticesFetchStatus: 'idle',
  totalPatientsFetchStatus: 'idle',
  weeklySalesFetchStatus: 'idle',
  weeklyOrdersFetchStatus: 'idle',
  orderBreakdownFetchStatus: 'idle',
  monthlyOrderCountFetchStatus: 'idle',
  weeklyIvrFetchStatus: 'idle',
  ivrBreakdownFetchStatus: 'idle',
  monthlyIvrCountFetchStatus: 'idle',
  weeklyRecordTxPercentageFetchStatus: 'idle'
} as DashboardState;

export const fetchActiveUsersKPIData = createAsyncThunk<any, { skipLoader?: boolean }>("fetchActiveUsersKPIData", async ({ skipLoader = false }) => {
  const response = await getActiveUsersKPIData({
    'x-skip-loader': skipLoader.toString(),
  });
  const v3Response = response.data;
  const activeUsersData = v3Response.data;
  return activeUsersData;
});

export const fetchActivePracticesKPIData = createAsyncThunk<any, { skipLoader?: boolean }>("fetchActivePracticesKPIData", async ({ skipLoader = false }) => {
  const response = await getActivePracticesKPIData({
    'x-skip-loader': skipLoader.toString(),
  });
  const v3Response = response.data;
  const activePracticesData = v3Response.data;
  return activePracticesData;
});

export const fetchTotalPatientsKPIData = createAsyncThunk<any, { skipLoader?: boolean }>("fetchTotalPatientsKPIData", async ({ skipLoader = false }) => {
  const response = await getTotalPatientsKPIData({
    'x-skip-loader': skipLoader.toString(),
  });
  const v3Response = response.data;
  const totalPatientsData = v3Response.data;
  return totalPatientsData;
});

export const fetchWeeklySalesKPIData = createAsyncThunk<any, { skipLoader?: boolean }>("fetchWeeklySalesKPIData", async ({ skipLoader = false }) => {
  const response = await getWeeklySalesKPIData({
    'x-skip-loader': skipLoader.toString(),
  });
  const v3Response = response.data;
  const weeklySalesData = v3Response.data;
  return weeklySalesData;
});

export const fetchWeeklyOrdersKPIData = createAsyncThunk<any, { skipLoader?: boolean }>("fetchWeeklyOrdersKPIData", async ({ skipLoader = false }) => {
  const response = await getWeeklyOrdersKPIData({
    'x-skip-loader': skipLoader.toString(),
  });
  const v3Response = response.data;
  const weeklySalesData = v3Response.data;
  return weeklySalesData;
});

export const fetchOrderBreakdownData = createAsyncThunk<any, { skipLoader?: boolean }>("fetchOrderBreakdownData", async ({ skipLoader = false }) => {
  const response = await getOrderBreakdownData({
    'x-skip-loader': skipLoader.toString(),
  });
  const v3Response = response.data;
  const weeklySalesData = v3Response.data;
  return weeklySalesData;
});

export const fetchIvrBreakdownData = createAsyncThunk<any, { skipLoader?: boolean }>("fetchIvrBreakdownData", async ({ skipLoader = false }) => {
  const response = await getIvrBreakdownData({
    'x-skip-loader': skipLoader.toString(),
  });
  const v3Response = response.data;
  const weeklySalesData = v3Response.data;
  return weeklySalesData;
});

export const fetchMonthlyOrderCountsTwelveMonths = createAsyncThunk<any, { skipLoader?: boolean }>("fetchMonthlyOrderCountsTwelveMonths", async ({ skipLoader = false }) => {
  const response = await getMonthlyOrderCountsTwelveMonths({
    'x-skip-loader': skipLoader.toString(),
  });
  const v3Response = response.data;
  const weeklySalesData = v3Response.data;
  return weeklySalesData;
});

export const fetchMonthlyIvrCountsTwelveMonths = createAsyncThunk<any, { skipLoader?: boolean }>("fetchMonthlyIvrCountsTwelveMonths", async ({ skipLoader = false }) => {
  const response = await getMonthlyIvrCountsTwelveMonths({
    'x-skip-loader': skipLoader.toString(),
  });
  const v3Response = response.data;
  const weeklySalesData = v3Response.data;
  return weeklySalesData;
});

export const fetchWeeklyRecordTxPercentage = createAsyncThunk<any, { skipLoader?: boolean }>("fetchWeeklyRecordTxPercentage", async ({ skipLoader = false }) => {
  const response = await getWeeklyRecordTxPercentage({
    'x-skip-loader': skipLoader.toString(),
  });
  const v3Response = response.data;
  const weeklySalesData = v3Response.data;
  return weeklySalesData;
});

// CS-1679 Start
export const searchUserKpiList = createAsyncThunk("search_user_kpi_list", async (data: SearchKpiRequest) => {
  const response = await getActiveUsersKPIDetailsList(data);
  return response;
});

export const searchPracticesKpiList = createAsyncThunk("search_practice_kpi_list", async (data: SearchKpiRequest) => {
  const response = await getActivePracticesKPIDetailsList(data);
  return response;
});

export const searchWeeklySalesKpiList = createAsyncThunk("search_weekly_sales_kpi_list", async (data: SearchKpiRequest) => {
  const response = await getWeeklySalesKPIDetailsList(data);
  return response;
});
// CS-1679 End

const dashboardSlice = createSlice({
  name: "dashboardSlice",
  initialState,
  reducers: {
    updateElementInDashboardSlice: updateElementValue,
    resetDashboardData: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActiveUsersKPIData.pending, (state) => {
        state.activeUsersFetchStatus = "loading";
      })
      .addCase(fetchActiveUsersKPIData.fulfilled, (state, action) => {
        state.activeUsersFetchStatus = "success";
        state.userStats = action.payload;
      })
      .addCase(fetchActiveUsersKPIData.rejected, (state, action) => {
        state.activeUsersFetchStatus = 'error';
      })
      .addCase(fetchActivePracticesKPIData.pending, (state) => {
        state.activePracticesFetchStatus = "loading";
      })
      .addCase(fetchActivePracticesKPIData.fulfilled, (state, action) => {
        state.activePracticesFetchStatus = "success";
        state.practiceStats = action.payload;
      })
      .addCase(fetchActivePracticesKPIData.rejected, (state, action) => {
        state.activePracticesFetchStatus = 'error';
      })
      .addCase(fetchTotalPatientsKPIData.pending, (state) => {
        state.totalPatientsFetchStatus = "loading";
      })
      .addCase(fetchTotalPatientsKPIData.fulfilled, (state, action) => {
        state.totalPatientsFetchStatus = "success";
        state.patientStats = action.payload;
      })
      .addCase(fetchTotalPatientsKPIData.rejected, (state, action) => {
        state.totalPatientsFetchStatus = 'error';
      })
      .addCase(fetchWeeklySalesKPIData.pending, (state) => {
        state.weeklySalesFetchStatus = "loading";
      })
      .addCase(fetchWeeklySalesKPIData.fulfilled, (state, action) => {
        state.weeklySalesFetchStatus = "success";
        state.salesStats = action.payload;
      })
      .addCase(fetchWeeklySalesKPIData.rejected, (state, action) => {
        state.weeklySalesFetchStatus = 'error';
      })
      .addCase(fetchWeeklyOrdersKPIData.pending, (state) => {
        state.weeklyOrdersFetchStatus = "loading";
      })
      .addCase(fetchWeeklyOrdersKPIData.fulfilled, (state, action) => {
        state.weeklyOrdersFetchStatus = "success";
        state.weeklyOrdersStats = action.payload;
      })
      .addCase(fetchWeeklyOrdersKPIData.rejected, (state, action) => {
        state.weeklyOrdersFetchStatus = 'error';
      })
      .addCase(fetchOrderBreakdownData.pending, (state) => {
        state.orderBreakdownFetchStatus = "loading";
      })
      .addCase(fetchOrderBreakdownData.fulfilled, (state, action) => {
        state.orderBreakdownFetchStatus = "success";
        state.orderBreakdownStats = action.payload;
      })
      .addCase(fetchOrderBreakdownData.rejected, (state, action) => {
        state.orderBreakdownFetchStatus = 'error';
      })
      .addCase(fetchIvrBreakdownData.pending, (state) => {
        state.ivrBreakdownFetchStatus = "loading";
      })
      .addCase(fetchIvrBreakdownData.fulfilled, (state, action) => {
        state.ivrBreakdownFetchStatus = "success";
        state.ivrBreakdownStats = action.payload;
      })
      .addCase(fetchIvrBreakdownData.rejected, (state, action) => {
        state.ivrBreakdownFetchStatus = 'error';
      })
      .addCase(fetchMonthlyOrderCountsTwelveMonths.pending, (state) => {
        state.monthlyOrderCountFetchStatus = "loading";
      })
      .addCase(fetchMonthlyOrderCountsTwelveMonths.fulfilled, (state, action) => {
        state.monthlyOrderCountFetchStatus = "success";
        state.monthlyOrderCountTwelveMonths = action.payload;
      })
      .addCase(fetchMonthlyOrderCountsTwelveMonths.rejected, (state, action) => {
        state.monthlyOrderCountFetchStatus = 'error';
      })
      .addCase(fetchMonthlyIvrCountsTwelveMonths.pending, (state) => {
        state.monthlyIvrCountFetchStatus = "loading";
      })
      .addCase(fetchMonthlyIvrCountsTwelveMonths.fulfilled, (state, action) => {
        state.monthlyIvrCountFetchStatus = "success";
        state.monthlyIvrCountTwelveMonths = action.payload;
      })
      .addCase(fetchMonthlyIvrCountsTwelveMonths.rejected, (state, action) => {
        state.monthlyIvrCountFetchStatus = 'error';
      })
      .addCase(fetchWeeklyRecordTxPercentage.pending, (state) => {
        state.weeklyRecordTxPercentageFetchStatus = "loading";
      })
      .addCase(fetchWeeklyRecordTxPercentage.fulfilled, (state, action) => {
        state.weeklyRecordTxPercentageFetchStatus = "success";
        state.weeklyRecordTxPercentageStats = action.payload;
      })
      .addCase(fetchWeeklyRecordTxPercentage.rejected, (state, action) => {
        state.weeklyRecordTxPercentageFetchStatus = 'error';
      })
  },
});

export const { updateElementInDashboardSlice, resetDashboardData } = dashboardSlice.actions;
export const useDashboardReducer = () => useAppSelector((state) => state.dashboardSlice);
export default dashboardSlice.reducer;

